import { useHotkey } from '@tanstack/react-hotkeys';
import { type ChangeEvent, useRef } from 'react';
import { InputFileAttachment, type InputFileAttachmentData } from '../attachments';
import { Button } from '../button';
import { Flex } from '../flex';
import { Form } from '../form';
import { HorizontalScroller } from '../horizontal-scroller';
import { Icon } from '../icons';
import { Space } from '../space';
import { TextArea, type TextAreaRef } from '../text-area';
import styles from './composite-input.module.css';

export type CompositeInputAttachment = {
  id: string;
  isLoading?: boolean;
} & InputFileAttachmentData;

export type CompositeInputProperties = {
  allowAttachments?: boolean;
  attachments?: CompositeInputAttachment[];
  onChange?: (value: string) => void;
  onRemoveAttachment?: (attachment: CompositeInputAttachment) => void;
  onSelectFiles?: (files: File[]) => void;
  placeholder?: string;
  value?: string;
};

const emptyAttachments: CompositeInputAttachment[] = [];

const CompositeInput = ({
  allowAttachments = true,
  attachments = emptyAttachments,
  onChange,
  onRemoveAttachment,
  onSelectFiles,
  placeholder,
  value,
}: CompositeInputProperties) => {
  const form = Form.useFormInstance();
  const fileInputReference = useRef<HTMLInputElement>(null);
  const visibleAttachments = allowAttachments ? attachments : emptyAttachments;
  const hasMessage = Boolean(value?.trim());
  const hasReadyAttachments = visibleAttachments.some((attachment) => !attachment.isLoading);
  const isUploadingAttachments = visibleAttachments.some((attachment) => attachment.isLoading);
  const canSend = !isUploadingAttachments && (hasMessage || hasReadyAttachments);
  const shouldShowSend = hasMessage || visibleAttachments.length > 0;
  const textAreaReference = useRef<TextAreaRef>(null);
  const textAreaDivReference = useRef<HTMLDivElement>(null);

  const handleAddAttachmentClick = () => {
    fileInputReference.current?.click();
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files ?? [])];
    event.target.value = '';

    if (files.length === 0) {
      return;
    }

    onSelectFiles?.(files);
  };

  useHotkey(
    'Enter',
    () => {
      if (canSend) {
        form.submit();
      }
    },
    { ignoreInputs: false, target: textAreaDivReference },
  );

  return (
    <Space block className={styles['composite-input']} orientation="vertical">
      {visibleAttachments.length > 0 ? (
        <HorizontalScroller ariaLabel="Attachments">
          {visibleAttachments.map((attachment) => (
            <InputFileAttachment
              attachment={attachment}
              key={attachment.id}
              onRemove={() => {
                onRemoveAttachment?.(attachment);
              }}
              size="small"
            />
          ))}
        </HorizontalScroller>
      ) : null}
      <div ref={textAreaDivReference}>
        <TextArea
          autoSize={{ minRows: 1 }}
          classNames={{
            root: styles.field,
            textarea: styles['text-area'],
          }}
          onChange={(event) => {
            onChange?.(event.target.value);
          }}
          placeholder={placeholder}
          ref={textAreaReference}
          value={value}
          variant="borderless"
        />
      </div>
      {allowAttachments || shouldShowSend ? (
        <Flex justify={allowAttachments ? 'space-between' : 'flex-end'}>
          {allowAttachments ? (
            <>
              <input
                className={styles['file-input']}
                multiple
                onChange={handleFileInputChange}
                ref={fileInputReference}
                type="file"
              />
              <Button
                aria-label="Add attachment"
                icon={<Icon icon="add-line" />}
                onClick={handleAddAttachmentClick}
                size="small"
                type="text"
              />
            </>
          ) : null}
          {shouldShowSend ? (
            <Button
              aria-label="Send"
              disabled={!canSend}
              htmlType="submit"
              icon={<Icon icon="send-line" />}
              onClick={() => {
                textAreaReference.current?.focus();
              }}
              size="small"
              type="primary"
            />
          ) : null}
        </Flex>
      ) : null}
    </Space>
  );
};

export default CompositeInput;
